import {
  AiChatArtifactContentPart,
  AiChatBuildDefinitionContentPart,
  AiChatMessageContent,
  AiChatTableContentPart,
} from '@server/ai';

/**
 * Helper to extract text content from the new structured message format.
 * Handles both old (string) and new (array of parts) formats.
 */
export function getTextContent(content: AiChatMessageContent): string {
  if (typeof content === 'string') {
    return content;
  }

  // Extract text from all text parts, ignore artifact parts
  return content
    .filter((part) => part.type === 'text')
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('');
}

/**
 * Extract artifact parts from structured content.
 * Returns array of artifacts with their metadata.
 */
export function getArtifactParts(
  content: AiChatMessageContent,
): Array<{ artifactId: string; versionId: string; title: string }> {
  if (typeof content === 'string') {
    return [];
  }

  return content
    .filter((part): part is AiChatArtifactContentPart => part.type === 'artifact')
    .map((part) => ({
      artifactId: part.artifactId,
      versionId: part.versionId,
      title: part.title,
    }));
}

/**
 * Build a map of versionId -> version number based on order of appearance in messages.
 * Used to display version numbers in preview boxes before artifacts are fetched.
 */
export function buildArtifactVersionMap(
  messages: Array<{ content: AiChatMessageContent }>,
): Map<string, number> {
  const versionMap = new Map<string, number>();
  const artifactVersionCounters = new Map<string, number>();

  messages.forEach((message) => {
    const artifacts = getArtifactParts(message.content);
    artifacts.forEach(({ artifactId, versionId }) => {
      // If we haven't seen this versionId before
      if (!versionMap.has(versionId)) {
        // Get the current version count for this artifactId (starts at 0)
        const currentCount = artifactVersionCounters.get(artifactId) || 0;
        const versionNumber = currentCount + 1;

        // Store the version number for this versionId
        versionMap.set(versionId, versionNumber);

        // Increment the counter for this artifactId
        artifactVersionCounters.set(artifactId, versionNumber);
      }
    });
  });

  return versionMap;
}

/**
 * Extract all unique artifacts from chat messages with their metadata.
 * Returns an array of artifacts with title and version count.
 */
export interface ArtifactSummary {
  artifactId: string;
  title: string;
  versionCount: number;
}

export function getAllArtifactsFromChat(
  messages: Array<{ content: AiChatMessageContent }>,
): ArtifactSummary[] {
  const artifactMap = new Map<string, { title: string; versionIds: Set<string> }>();

  messages.forEach((message) => {
    const artifacts = getArtifactParts(message.content);
    artifacts.forEach(({ artifactId, versionId, title }) => {
      if (!artifactMap.has(artifactId)) {
        artifactMap.set(artifactId, {
          title: title.replace(/^"|"$/g, ''), // Remove surrounding quotes
          versionIds: new Set(),
        });
      }
      artifactMap.get(artifactId)!.versionIds.add(versionId);
    });
  });

  // Convert to array and include version count
  return Array.from(artifactMap.entries()).map(([artifactId, data]) => ({
    artifactId,
    title: data.title,
    versionCount: data.versionIds.size,
  }));
}

/**
 * Extract table parts from structured content.
 * Returns array of table IDs.
 */
export function getTableParts(content: AiChatMessageContent): Array<{ tableId: string }> {
  if (typeof content === 'string') {
    return [];
  }

  return content
    .filter((part): part is AiChatTableContentPart => part.type === 'table')
    .map((part) => ({ tableId: part.tableId }));
}

/**
 * Extract build definition parts from structured content.
 * Returns array of build definitions with their metadata.
 */
export function getBuildDefinitionParts(
  content: AiChatMessageContent,
): Array<Omit<AiChatBuildDefinitionContentPart, 'type'>> {
  if (typeof content === 'string') {
    return [];
  }

  return content
    .filter(
      (part): part is AiChatBuildDefinitionContentPart => part.type === 'build_definition',
    )
    .map((part) => ({
      buildDefinitionId: part.buildDefinitionId,
      status: part.status,
      text: part.text,
    }));
}

/**
 * Regex pattern for table placeholders in streaming text.
 * Matches [[TABLE:xxx]] where xxx is the table ID.
 */
const TABLE_PLACEHOLDER_REGEX = /\[\[TABLE:([^\]]+)\]\]/g;

/**
 * Extract table placeholders from streaming text.
 * Returns array of table IDs found in the text.
 */
export function extractTablePlaceholders(text: string): string[] {
  const matches = text.matchAll(TABLE_PLACEHOLDER_REGEX);
  return Array.from(matches).map((match) => match[1]);
}

/**
 * Parse text content and split it into segments of text and table placeholders.
 * Used for rendering streaming content with inline table links.
 */
export type TextOrTableSegment =
  | { type: 'text'; content: string }
  | { type: 'table'; tableId: string };

export function parseTextWithTablePlaceholders(text: string): TextOrTableSegment[] {
  const segments: TextOrTableSegment[] = [];
  let lastIndex = 0;

  const regex = /\[\[TABLE:([^\]]+)\]\]/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the placeholder
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    // Add the table placeholder
    segments.push({
      type: 'table',
      tableId: match[1],
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last placeholder
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return segments;
}

/**
 * Check if content contains any table parts (either structured or placeholders).
 */
export function hasTableContent(content: AiChatMessageContent): boolean {
  if (typeof content === 'string') {
    return TABLE_PLACEHOLDER_REGEX.test(content);
  }
  return content.some((part) => part.type === 'table');
}
